import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import WhatsAppGroupWelcomeDialog from "./components/WhatsAppGroupWelcomeDialog";
import DepoimentoPendingDialog from "./components/DepoimentoPendingDialog";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { supabase } from "@/integrations/supabase/client";

/**
 * Defensive boot cleanup: if a stale/invalid Supabase auth refresh_token
 * is stored in localStorage, the client retries forever, exhausting fetch
 * and breaking every other request (login spinner, public-anuncios skeleton).
 * On boot, validate the session; if refresh fails, sign out locally to
 * clear the bad token and stop the loop.
 */
const useAuthSelfHeal = () => {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (cancelled) return;
        const msg = String(error?.message || "");
        const isRefreshErr =
          /refresh.*token|Invalid Refresh Token|Refresh Token Not Found|Failed to fetch/i.test(msg);
        if (error && isRefreshErr) {
          await supabase.auth.signOut({ scope: "local" }).catch(() => {});
        } else if (!data?.session) {
          // Clean any leftover supabase auth keys that could trigger refresh loops
          try {
            for (let i = localStorage.length - 1; i >= 0; i--) {
              const k = localStorage.key(i);
              if (k && k.startsWith("sb-") && k.endsWith("-auth-token")) {
                const raw = localStorage.getItem(k);
                if (raw && raw.includes("refresh_token") && !raw.includes("access_token")) {
                  localStorage.removeItem(k);
                }
              }
            }
          } catch { /* ignore */ }
        }
      } catch {
        await supabase.auth.signOut({ scope: "local" }).catch(() => {});
      }
    })();
    return () => { cancelled = true; };
  }, []);
};

// Lazy-load heavy routes that are not needed on initial page load
const TrocaCoins = lazy(() => import("./pages/TrocaCoins"));
const Admin = lazy(() => import("./pages/Admin"));
const ClientArea = lazy(() => import("./pages/ClientArea"));
const ItemPage = lazy(() => import("./pages/ItemPage"));
const Avaliar = lazy(() => import("./pages/Avaliar"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Depoimentos = lazy(() => import("./pages/Depoimentos"));
const DepoimentoPage = lazy(() => import("./pages/DepoimentoPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const LazyFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="font-pixel text-primary text-xs animate-pulse">Carregando...</div>
  </div>
);

const GlobalIntermediacaoNotification = lazy(() => import("./components/GlobalIntermediacaoNotification"));

const GlobalDialogs = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return (
    <>
      <WhatsAppGroupWelcomeDialog />
      <DepoimentoPendingDialog />
      <Suspense fallback={null}>
        <GlobalIntermediacaoNotification />
      </Suspense>
    </>
  );
};

const App = () => {
  useAuthSelfHeal();
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <GlobalDialogs />
        <CookieConsentBanner />
        <Suspense fallback={<LazyFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/troca-coins" element={<TrocaCoins />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/cliente" element={<ClientArea />} />
            <Route path="/item/:id" element={<ItemPage />} />
            <Route path="/avaliar/:id" element={<Avaliar />} />
            <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/depoimentos" element={<Depoimentos />} />
            <Route path="/depoimento/:transacaoId" element={<DepoimentoPage />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
